import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Button as BoostrapButton, Form, InputGroup } from 'react-bootstrap';
import { isValidField, isValidSchema } from 'utils/schema/utils';
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import * as Yup from 'yup';
import { useLazyQuery } from '@apollo/client';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import ModalUtils from 'utils/ModalUtils';
import { FetchPolicy } from 'utils/enum/Core';
import VinScanner from 'components/widgets/inventory/VinScanner';

const useStyles = makeStyles(() => ({
    vinInput: {
        height: '35px',
    },
}));

const VinDecoderInput = ({ initialValue, onVinDecoded }) => {
    const initialState = {
        vin: initialValue || '',
    };

    const classes = useStyles();
    const [state, setState] = useState(initialState);
    const { vin } = state;

    const [loadVinData, { loading: vinLoading }] = useLazyQuery(InventoryQuery.GET_VEHICLE_DATA, {
        onCompleted: (res) => {
            if (res.decodeVin) {
                const {
                    make: vinMake,
                    model: vinModel,
                    trim: vinTrim,
                    year: vinYear,
                    engines: vinEngines,
                } = res.decodeVin;

                onVinDecoded({
                    vin: state.vin,
                    make: vinMake,
                    model: vinModel,
                    trim: vinTrim?.[0] || '',
                    year: vinYear,
                    engines: vinEngines,
                    engine: vinEngines?.[0] || '',
                });
            }
        },
        onError: () => {
            ModalUtils.errorMessage(null, 'There was an error trying to decoding this VIN.');
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const validationSchema = Yup.object().shape({
        vin: Yup.string()
            .notRequired()
            .length(17, 'VIN must be exactly 17 characters'),
    });

    const isValidData = isValidSchema(validationSchema, state);

    const { errors, isValid } = isValidData;

    const onChangeValue = (e) => {
        const { name, value } = e.target;
        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const onDecodeVin = async () => {
        if (!isValid || !vin) {
            ModalUtils.errorMessage(null, 'Please check the VIN number.');
            return;
        }

        await loadVinData({
            variables: {
                vin: state.vin,
            },
        });
    };

    useEffect(() => {
        if ((vin && !isValid) || (vin.length === 0)) {
            onVinDecoded({
                vin: state.vin,
                make: '',
                model: '',
                trim: '',
                year: 0,
                engines: [],
                engine: '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin, isValid]);

    return (
        <InputGroup size="sm">
            <Form.Control
                onChange={onChangeValue}
                className={clsx(classes.vinInput, isValidField(vin.length > 0 && errors, 'vin') ? 'invalid-field' : '')}
                value={vin}
                type="text"
                placeholder=""
                size="sm"
                name="vin"
                disabled={vinLoading}
            />
            <InputGroup.Append>
                <BoostrapButton
                    variant="primary"
                    size="sm"
                    onClick={onDecodeVin}
                    disabled={Boolean(isValidField(errors, 'vin')) || vinLoading || !vin}
                >
                    Decode
                </BoostrapButton>
                <VinScanner
                    shortButton
                    vinNumber={vin}
                    loading={vinLoading}
                    onChange={(_, vinNumber) => onChangeValue({ target: { name: 'vin', value: vinNumber } })}
                    decodeVin={onDecodeVin}
                />
            </InputGroup.Append>
        </InputGroup>
    );
};

VinDecoderInput.propTypes = {
    onVinDecoded: PropTypes.func.isRequired,
    initialValue: PropTypes.string,
};

VinDecoderInput.defaultProps = {
    initialValue: '',
};

export default VinDecoderInput;
